"use client";

import { BrandMark, BrandMascot } from "@/components/brand";
import { CinematicModal } from "@/components/ui/CinematicModal";
import { trackUpgradeIntent } from "@/lib/analytics/posthog-helpers";
import {
  cinematicModalPrimaryButtonClass,
  cinematicModalSubtitleClass,
  cinematicModalTitleClass,
} from "@/lib/cinematicModalStyles";
import { buildTrackedPaywallUrl } from "@/lib/paywallUrl";
import { Check } from "lucide-react";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";

interface PastConversationsUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
}

const PastConversationsUpgradeModal: React.FC<PastConversationsUpgradeModalProps> = ({
  isOpen,
  onClose,
  userEmail,
}) => {
  const router = useRouter();
  const posthog = usePostHog();

  const handleUpgrade = () => {
    trackUpgradeIntent(posthog, "past_conversations_paywall", {
      email: userEmail,
      feature: "past_conversations",
    });

    router.push(buildTrackedPaywallUrl({ source: "past_conversations_paywall" }));
  };

  return (
    <CinematicModal
      isOpen={isOpen}
      onClose={onClose}
      titleId="past-conversations-dialog-title"
      maxWidth="3xl"
      contentClassName="p-0 sm:p-0"
    >
      <div className="grid sm:grid-cols-[280px_minmax(0,1fr)]">
        <div className="hidden items-center overflow-hidden border-r border-white/10 bg-white/[0.025] sm:flex">
          <BrandMascot pose="teaching" className="h-auto w-[280px] object-contain" sizes="280px" />
        </div>

        <div className="p-5 sm:p-6">
          <div className="mb-4 sm:hidden">
            <BrandMark size={56} className="h-14 w-14 object-contain" alt="" />
          </div>

          <div className="pr-8">
            <h2 id="past-conversations-dialog-title" className={cinematicModalTitleClass}>
              Pick up where you left off
            </h2>
            <p className={cinematicModalSubtitleClass}>Past conversations</p>
          </div>

          <p className="mt-4 text-sm leading-relaxed text-white/75">
            Access your conversation history and continue practicing where you left off.
          </p>

          <div className="mt-4 border-y border-white/10 py-4 text-sm text-white/70">
            <h3 className="font-semibold text-orange-200">Premium benefits</h3>
            <ul className="mt-3 space-y-2">
              {[
                "Review and continue past conversations",
                "Unlimited session time",
                "Track learning progress over time",
                "Priority support and early access",
              ].map((benefit) => (
                <li key={benefit} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-orange-300" aria-hidden="true" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 flex flex-col gap-3">
            <button
              type="button"
              onClick={handleUpgrade}
              className={`${cinematicModalPrimaryButtonClass} w-full`}
            >
              Upgrade to PRO
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-11 w-full items-center justify-center px-2 text-sm font-semibold text-white/55 transition-colors hover:text-white"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </CinematicModal>
  );
};

export default PastConversationsUpgradeModal;
